import { useEffect, useMemo, type ReactElement } from 'react';
import { Alert, Box, Group, Loader, Stack, Text } from '@mantine/core';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import InsightsRailCard from '@/shared/surfaces/InsightsRailCard';
import type { StatItem } from '@/design/StatBand';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { formatPercent, formatUsd } from '@/shared/analytics/MetricFormatting';
import type { VizAccent } from '@/design/VizAccents';
import { CostForecastViewModel } from './CostForecastViewModel';
import CostProjectionChart from './CostProjectionChart';
import SpendCostRecommendationsPanel from './SpendCostRecommendationsPanel';

/** The Forecast screen's accent — shared by the header, projection chart, and recommendations so the page reads as one surface. */
const FORECAST_ACCENT: VizAccent = 'indigo';

/** Spend › Cost › Forecast: projected month-end spend (working-day run rate) with license-vs-API and cheaper-model recommendations. */
export default function SpendCostForecastPage(): ReactElement {
    const vm = useViewModel(CostForecastViewModel);
    const surface = useSurface('token-usage');
    const { snapshot } = useGlobalFilters();
    const filterKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

    useEffect(() => {
        void vm.reload();
    }, [vm, filterKey]);

    if (surface === undefined) return <TopicNotFound />;

    const summary = vm.result?.summary ?? null;
    const deltaPct = summary?.deltaPctVsLastMonth ?? null;
    const deltaDisplay =
        deltaPct === null ? '—'
        : deltaPct > 9.99 ? '>+999%'
        : `${deltaPct >= 0 ? '+' : '−'}${formatPercent(Math.abs(deltaPct))}`;
    const stats: StatItem[] =
        summary === null ?
            []
        :   [
                { label: 'Projected this month', value: formatUsd(summary.projectedMonth), hint: 'metered run rate + flat seats' },
                { label: 'Month to date', value: formatUsd(summary.mtdActual) },
                { label: 'vs last month', value: deltaDisplay, hint: summary.lastMonthTotal === null ? undefined : `was ${formatUsd(summary.lastMonthTotal)}` },
                { label: 'Run rate', value: formatUsd(summary.avgActiveDay), hint: 'per active day' },
            ];

    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <PageHeaderComponent
                    icon={CalendarClock}
                    eyebrow="Cost"
                    title="Forecast"
                    subtitle="Projected month-end spend from the working-day run rate, with license-vs-API and cheaper-model recommendations."
                />

                <Box mt="lg">
                    {vm.loading && vm.result === null ?
                        <Group justify="center" py={64}>
                            <Loader size="sm" />
                        </Group>
                    : vm.error !== null ?
                        <Alert color="red" icon={<AlertTriangle size={16} />} title="Could not load forecast">
                            {vm.error}
                        </Alert>
                    : vm.result === null ?
                        <Text size="sm" c="dimmed">
                            No spend data in the current month yet.
                        </Text>
                    :   <SideRailPageLayout
                            rail={
                                <Stack gap="md">
                                    <TopicSummaryPanel stats={stats} />
                                    <InsightsRailCard derived={vm.derivedInsights} accent={FORECAST_ACCENT} />
                                </Stack>
                            }
                        >
                            <Stack gap="lg">
                                <CostProjectionChart
                                    result={vm.result}
                                    comparison={vm.comparison}
                                    accent={FORECAST_ACCENT}
                                    lookbackMonths={vm.lookbackMonths}
                                    onLookbackChange={(months) => vm.setLookbackMonths(months)}
                                />
                                <SpendCostRecommendationsPanel recommendations={vm.recommendations} accent={FORECAST_ACCENT} />
                            </Stack>
                        </SideRailPageLayout>
                    }
                </Box>
            </Box>
        </SurfaceAssistScope>
    );
}
